import { Button, ButtonPropsShadcn } from "@/modern/components/ui/button-shadcn";
import { Dialog, DialogClose, DialogTrigger } from "@/modern/components/ui/dialog-shadcn";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";
import { DialogTriggerProps } from "@radix-ui/react-dialog";
import { DropdownMenuItemProps } from "@radix-ui/react-dropdown-menu";
import { LucideProps, MoreVertical } from "lucide-react";
import { ForwardRefExoticComponent, RefAttributes, useRef, useCallback, useState } from "react";
import { MenuItemModalHeaderVariant, ModalItem, SharedModal, SharedModalItemProps } from "./shared-modal-component";
import { EntityRecord } from "@/types/entity";

// Re-export shared components for backward compatibility
export { ModalItem, SharedModalBody, SharedModalContent, SharedModalDescription, SharedModal as SharedModalFrostedGlass, SharedModalHeader, SharedModalTitle } from "./shared-modal-component";

export type { MenuItemModalHeaderVariant, ModalItemProps, SharedModalItemProps } from "./shared-modal-component";

export type MenuComponentProps = { record?: EntityRecord; onFinish?: () => void } & Record<string, unknown>;

export type MenuItem =
  | (SharedModalItemProps & {
      itemVariant: "modal";
      modalClassName?: string;
      classNameContent?: string;
      menuItemModalHeaderVariant: MenuItemModalHeaderVariant;
      menuText: string;
      Icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
      Component: (props: MenuComponentProps) => JSX.Element;
      componentProps?: Record<string, unknown>;
      dialogTriggerProps?: DialogTriggerProps;
      dropdownMenuItemProps?: DropdownMenuItemProps;
      noWrapper?: boolean;
    })
  | {
      itemVariant: "button";
      dropdownMenuItemProps?: DropdownMenuItemProps;
      menuText: string;
      Component: (props: MenuComponentProps) => JSX.Element;
    }
  | {
      itemVariant: "separator";
    };

export const MoreMenu = ({
  menuTitle,
  record,
  menuItems,
  disabled,
  triggerButtonClassName,
  triggerButtonVariant = "ghost",
  triggerButtonSize = "icon"
}: {
  menuTitle?: string;
  record?: EntityRecord;
  menuItems: MenuItem[];
  disabled?: boolean;
  triggerButtonClassName?: string;
  triggerButtonVariant?: ButtonPropsShadcn["variant"];
  triggerButtonSize?: ButtonPropsShadcn["size"];
}) => {
  const [open, setOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<string>("");
  const dialogCloseRef = useRef<HTMLButtonElement>(null);

  const handleFinish = useCallback(() => {
    setOpen(false);
    dialogCloseRef.current?.click();
  }, []);

  const item = menuItems.find((item) => "itemVariant" in item && "menuText" in item && item?.menuText === activeItem) || menuItems[0];
  type ModalMenuItem = Extract<MenuItem, { itemVariant: "modal" }>;
  const modalItem = (item.itemVariant === "modal" ? item : menuItems.find((menuItem) => menuItem.itemVariant === "modal")) as ModalMenuItem | undefined;
  const modalContainerProps = modalItem ? (({ Component: _Component, componentProps: _componentProps, ...rest }: ModalMenuItem) => rest)(modalItem) : undefined;
  const ModalComponent = modalItem?.Component;
  const modalChildProps = modalItem?.componentProps;

  return (
    <Dialog modal={true}>
      <DialogClose ref={dialogCloseRef} className="hidden" />
      <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger disabled={disabled} asChild>
          <Button variant={triggerButtonVariant} size={triggerButtonSize} className={triggerButtonClassName}>
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className=" p-1">
          {menuTitle && (
            <>
              <DropdownMenuLabel className="text-sm px-1 py-1">{menuTitle}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {menuItems.map((props, index) => {
            if (props.itemVariant === "modal") {
              return (
                <DialogTrigger onMouseDown={() => setActiveItem(props.menuText)} key={props.menuText} {...props.dialogTriggerProps} asChild>
                  <DropdownMenuItem {...props.dropdownMenuItemProps}>
                    {"Icon" in props && props.Icon && <props.Icon className="size-4 mr-1" />}
                    {props.menuText}
                  </DropdownMenuItem>
                </DialogTrigger>
              );
            }

            if (props.itemVariant === "button") {
              return (
                <DropdownMenuItem
                  {...props.dropdownMenuItemProps}
                  className={cn(
                    "w-full relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
                    props.dropdownMenuItemProps?.className
                  )}
                  key={props.menuText}
                >
                  <props.Component record={record} onFinish={handleFinish} />
                </DropdownMenuItem>
              );
            }
            return <DropdownMenuSeparator key={index} />;
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <SharedModal className={(modalItem && modalItem.modalClassName) || undefined}>
        {modalItem &&
          ModalComponent &&
          modalContainerProps &&
          (modalItem.noWrapper ? (
            <ModalComponent record={record} onFinish={handleFinish} {...(modalChildProps || {})} />
          ) : (
            <ModalItem record={record} {...modalContainerProps}>
              <ModalComponent record={record} onFinish={handleFinish} {...(modalChildProps || {})} />
            </ModalItem>
          ))}
      </SharedModal>
    </Dialog>
  );
};
